/**
 * WEB-16 срез 1: контракт поля «ответ на сообщение» (`replyTo`).
 *
 * Поле едет ВНУТРИ шифруемого тела, рядом с `body` и `ts` — сервер его не
 * видит и не должен: кто кому ответил, это содержание переписки, а не
 * метаданные доставки. Поэтому бэкенда и миграции у этой фичи нет вовсе.
 *
 * 🔴 Снимок (`author`, `excerpt`) — это СЛОВА ОТПРАВИТЕЛЯ, а не доказательство.
 * Расшифровка подтверждает, кто прислал ЭТО сообщение, и молчит о цитате
 * внутри: в комнате участник технически может приписать коллеге фразу,
 * которой тот не писал. Отсюда правило показа (срез 2): если оригинал есть
 * локально — рисуем ЕГО, снимок игнорируем; снимок остаётся запасным путём
 * для тех, у кого оригинала нет (вошёл позже, другое устройство, истёк срок
 * хранения). Поэтому же по снимку нельзя строить ничего, кроме текста: ни
 * ссылки на профиль, ни значка «проверено», ни прав.
 *
 * 🔴 Правила ОДНИ И ТЕ ЖЕ на отправке и на приёме, и это не симметрия ради
 * красоты: кап, который соблюдает только честный отправитель, не защищает
 * никого — чужой клиент пришлёт что угодно.
 */

/** Кап текста цитаты и имени, в СИМВОЛАХ (code points, а не UTF-16 units:
 *  иначе эмодзи режется пополам и получается мусор). */
export const MAX_REPLY_EXCERPT = 120;
export const MAX_REPLY_AUTHOR = 120;
/** Кап id оригинала. Его выдаёт клиент (UUID), 100 знаков — с большим
 *  запасом; смысл капа не в форме, а в том, чтобы чужой клиент не положил
 *  килобайт в ключ, по которому мы ищем в хранилище. */
export const MAX_REPLY_MESSAGE_ID = 100;

/** Чем подписывается ответ на сообщение-файл: тело у него пустое, а «ответ на
 *  пустоту» человек прочитать не может. Ключи, имена и метаданные вложения в
 *  цитату НЕ копируются — только эта подпись. */
export const ATTACHMENT_EXCERPT = 'Вложение';

// Управляющие символы: C0 без пробельных, C1, DEL, арабская метка и
// переключатели направления письма. 🔴 Таблицей диапазонов, а НЕ регуляркой с
// экранированием: инструменты этого проекта превращают последовательности вида
// u-плюс-код в живые символы при записи файла, и в исходнике оказывались
// невидимые управляющие байты — их не видно ни в обзоре правок, ни глазами.
//
// 🔴 Пробельные (табуляция, переносы) сюда НЕ входят намеренно: их заменяет
// пробел, а не снимает. Снеси их — и «строка<CR>вторая» склеилась бы в
// «строкавторая», то есть снимок врал бы о чужом тексте (находка ревью, P2).
const CONTROL_RANGES = [
  [0x00, 0x08], [0x0e, 0x1f], [0x7f, 0x9f], // C0 без пробельных, DEL, C1
  [0x061c, 0x061c],                          // ARABIC LETTER MARK
  [0x200e, 0x200f], [0x202a, 0x202e], [0x2066, 0x2069], // метки и разворот
];

// NEL (0x85) — перенос строки, но в JS он не входит в `\s`: без этой строки
// его снял бы фильтр управляющих и склеил соседние слова.
const NEL = 0x85;

const isSpace = (ch) => /\s/.test(ch) || ch.codePointAt(0) === NEL;
const isControl = (ch) => {
  const code = ch.codePointAt(0);
  return CONTROL_RANGES.some(([from, to]) => code >= from && code <= to);
};

function clip(value, max) {
  if (typeof value !== 'string') return '';
  // Цитата показывается ОДНОЙ строкой: пробельные становятся пробелом,
  // управляющие исчезают, лишние пробелы схлопываются.
  const flat = [...value]
    .map((ch) => {
      if (isSpace(ch)) return ' ';
      return isControl(ch) ? '' : ch;
    })
    .join('')
    .replace(/ +/g, ' ')
    .trim();
  const chars = [...flat];
  return chars.length <= max ? flat : chars.slice(0, max).join('');
}

/**
 * Привести `replyTo` к разрешённой форме. Возвращает НОВЫЙ объект ровно из
 * трёх разрешённых ключей или `null`, если цитаты нет или она негодная.
 *
 * @param raw — что положил отправитель (на приёме — из чужого шифротекста).
 * @param selfMessageId — id самого сообщения: ответ на себя бессмыслен и даёт
 *        цикл при переходе к оригиналу.
 *
 * 🔴 Негодное поле отбрасывается ЦЕЛИКОМ, а сообщение показывается как
 * обычное. Бросать здесь нельзя: разбор конверта не смеет падать из-за
 * цитаты — иначе один кривой клиент останавливает догон и двигает курсоры.
 */
export function normalizeReplyTo(raw, { selfMessageId = null } = {}) {
  // Массив — это не «одна цитата»: список цитат мы не показываем и не хотим,
  // чтобы он вообще доезжал до ленты.
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const { messageId } = raw;
  // Без приведения типов: число или объект в id — не «просто другой формат»,
  // а чужая форма, по которой мы полезли бы искать в хранилище.
  if (typeof messageId !== 'string') return null;
  const id = messageId.trim();
  if (!id || id.length > MAX_REPLY_MESSAGE_ID) return null;
  if (selfMessageId && id === selfMessageId) return null;
  // Собирается НОВЫЙ объект: лишние ключи чужого клиента (в том числе
  // вложенный `replyTo` оригинала) не переносятся — цепочки цитат не растут,
  // а будущие опасные поля не протаскиваются через нас в хранилище.
  return {
    messageId: id,
    author: clip(raw.author, MAX_REPLY_AUTHOR),
    excerpt: clip(raw.excerpt, MAX_REPLY_EXCERPT),
  };
}

/**
 * Снимок для ответа на сообщение из СВОЕЙ ленты. Отдельная функция, потому
 * что здесь решается, что вообще попадёт в чужое хранилище.
 *
 * @param message — строка ленты: {messageId, text, attachments}
 * @param author — отображаемое имя автора оригинала. 🔴 Не «Вы»: у получателя
 *        относительное имя означало бы другого человека.
 */
export function replySnapshot(message, author) {
  if (!message?.messageId) return null;
  // Сообщение-файл приходит с пустым телом — подписываем его, а не отправляем
  // пустую цитату.
  const excerpt = message.text?.trim()
    ? message.text
    : (message.attachments?.length ? ATTACHMENT_EXCERPT : '');
  return normalizeReplyTo({ messageId: message.messageId, author, excerpt });
}
